import React, { useMemo, useState } from 'react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Select } from '../components/ui/Select';
import { DatePicker } from '../components/ui/DatePicker';
import { useBanking } from '../context/BankingContext';
import { useData } from '../context/DataContext';
import { formatCurrency, formatDate } from '../utils/calculations';
import { BankAccount, Transaction } from '../types/banking';
import { CreditCardIcon, ArrowLeftIcon, FilterIcon, XIcon } from 'lucide-react';
type FilterType = 'all' | 'month' | 'quarter' | 'year' | 'custom';
export function CashFlow() {
  const {
    selectedCompany
  } = useData();
  const {
    getAccountsByCompanyId,
    getTransactionsByAccountId
  } = useBanking();
  const [selectedAccount, setSelectedAccount] = useState<BankAccount | null>(null);
  // Filter states
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [selectedMonth, setSelectedMonth] = useState<string>(new Date().getMonth().toString());
  const [selectedYear, setSelectedYear] = useState<string>(new Date().getFullYear().toString());
  const [selectedQuarter, setSelectedQuarter] = useState<string>('1');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const accounts = selectedCompany ? getAccountsByCompanyId(selectedCompany.id) : [];
  const allTransactions = selectedAccount ? getTransactionsByAccountId(selectedAccount.id) : [];
  // Filter transactions logic
  const filteredTransactions = useMemo(() => {
    if (filterType === 'all') return allTransactions;
    return allTransactions.filter(txn => {
      const txnDate = new Date(txn.date);
      const year = parseInt(selectedYear);
      switch (filterType) {
        case 'month':
          return txnDate.getMonth() === parseInt(selectedMonth) && txnDate.getFullYear() === year;
        case 'quarter':
          const quarter = Math.floor(txnDate.getMonth() / 3) + 1;
          return quarter === parseInt(selectedQuarter) && txnDate.getFullYear() === year;
        case 'year':
          return txnDate.getFullYear() === year;
        case 'custom':
          if (!startDate || !endDate) return true;
          return txnDate >= startDate && txnDate <= endDate;
        default:
          return true;
      }
    });
  }, [allTransactions, filterType, selectedMonth, selectedYear, selectedQuarter, startDate, endDate]);
  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      INCOME: 'text-green-600 bg-green-50',
      FOOD_AND_DRINK: 'text-orange-600 bg-orange-50',
      TRANSPORTATION: 'text-blue-600 bg-blue-50',
      GENERAL_SERVICES: 'text-purple-600 bg-purple-50',
      GENERAL_MERCHANDISE: 'text-pink-600 bg-pink-50',
      TRANSFER: 'text-gray-600 bg-gray-50',
      TRAVEL: 'text-indigo-600 bg-indigo-50'
    };
    return colors[category] || 'text-gray-600 bg-gray-50';
  };
  const resetFilters = () => {
    setFilterType('all');
    setStartDate(null);
    setEndDate(null);
    setSelectedMonth(new Date().getMonth().toString());
    setSelectedYear(new Date().getFullYear().toString());
  };
  if (selectedAccount) {
    return <div className="space-y-4 sm:space-y-6" data-id="element-626">
        {/* Back button and account header */}
        <div className="flex items-center gap-4" data-id="element-627">
          <Button variant="ghost" onClick={() => setSelectedAccount(null)} leftIcon={<ArrowLeftIcon className="w-4 h-4" data-id="element-629" />} className="text-sm sm:text-base" data-id="element-628">
            <span className="hidden sm:inline" data-id="element-630">Back to Accounts</span>
            <span className="sm:hidden" data-id="element-631">Back</span>
          </Button>
        </div>

        {/* Account summary */}
        <Card data-id="element-632">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4" data-id="element-633">
            <div className="flex items-center gap-3 sm:gap-4" data-id="element-634">
              <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center ${selectedAccount.type === 'checking' ? 'bg-blue-100' : 'bg-purple-100'}`} data-id="element-635">
                {selectedAccount.type === 'checking' ? <div className={`w-5 h-5 sm:w-6 sm:h-6 ${selectedAccount.type === 'checking' ? 'text-blue-600' : 'text-purple-600'}`} data-id="element-636" /> : <CreditCardIcon className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" data-id="element-637" />}
              </div>
              <div data-id="element-638">
                <h2 className="text-lg sm:text-xl font-bold text-gray-900" data-id="element-639">
                  {selectedAccount.name}
                </h2>
                <p className="text-xs sm:text-sm text-gray-500" data-id="element-640">
                  {selectedAccount.institution_name} •••• {selectedAccount.mask}
                </p>
              </div>
            </div>
            <div className="text-left sm:text-right" data-id="element-641">
              <p className="text-xs sm:text-sm text-gray-500 mb-1" data-id="element-642">
                {selectedAccount.type === 'checking' ? 'Current Balance' : 'Current Balance'}
              </p>
              <p className={`text-xl sm:text-2xl font-bold ${selectedAccount.current_balance >= 0 ? 'text-gray-900' : 'text-red-600'}`} data-id="element-643">
                {formatCurrency(Math.abs(selectedAccount.current_balance))}
                {selectedAccount.type === 'credit_card' && selectedAccount.current_balance < 0 && <span className="text-xs sm:text-sm text-gray-500 ml-2" data-id="element-644">
                      owed
                    </span>}
              </p>
              {selectedAccount.type === 'credit_card' && <p className="text-xs sm:text-sm text-gray-500 mt-1" data-id="element-645">
                  {formatCurrency(selectedAccount.available_balance)} available
                </p>}
            </div>
          </div>
        </Card>

        {/* Filters */}
        <Card className="p-4" data-id="element-646">
          <div className="flex flex-col gap-4" data-id="element-647">
            <div className="flex flex-wrap items-center gap-2" data-id="element-648">
              <Button variant={filterType === 'all' ? 'primary' : 'secondary'} size="sm" onClick={() => setFilterType('all')} data-id="element-649">
                All Time
              </Button>
              <Button variant={filterType === 'month' ? 'primary' : 'secondary'} size="sm" onClick={() => setFilterType('month')} data-id="element-650">
                By Month
              </Button>
              <Button variant={filterType === 'quarter' ? 'primary' : 'secondary'} size="sm" onClick={() => setFilterType('quarter')} data-id="element-651">
                By Quarter
              </Button>
              <Button variant={filterType === 'year' ? 'primary' : 'secondary'} size="sm" onClick={() => setFilterType('year')} data-id="element-652">
                By Year
              </Button>
              <Button variant={filterType === 'custom' ? 'primary' : 'secondary'} size="sm" onClick={() => setFilterType('custom')} data-id="element-653">
                Custom Range
              </Button>

              {filterType !== 'all' && <Button variant="ghost" size="sm" onClick={resetFilters} leftIcon={<XIcon className="w-4 h-4" data-id="element-655" />} className="ml-auto text-gray-500 hover:text-gray-700" data-id="element-654">
                  Clear Filters
                </Button>}
            </div>

            {filterType !== 'all' && <div className="flex flex-wrap items-center gap-4 pt-4 border-t border-gray-100" data-id="element-656">
                {filterType === 'month' && <>
                    <div className="w-40" data-id="element-657">
                      <Select label="Month" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} options={[{
                  value: '0',
                  label: 'January'
                }, {
                  value: '1',
                  label: 'February'
                }, {
                  value: '2',
                  label: 'March'
                }, {
                  value: '3',
                  label: 'April'
                }, {
                  value: '4',
                  label: 'May'
                }, {
                  value: '5',
                  label: 'June'
                }, {
                  value: '6',
                  label: 'July'
                }, {
                  value: '7',
                  label: 'August'
                }, {
                  value: '8',
                  label: 'September'
                }, {
                  value: '9',
                  label: 'October'
                }, {
                  value: '10',
                  label: 'November'
                }, {
                  value: '11',
                  label: 'December'
                }]} data-id="element-658" />
                    </div>
                    <div className="w-32" data-id="element-659">
                      <Select label="Year" value={selectedYear} onChange={e => setSelectedYear(e.target.value)} options={[{
                  value: '2024',
                  label: '2024'
                }, {
                  value: '2023',
                  label: '2023'
                }]} data-id="element-660" />
                    </div>
                  </>}

                {filterType === 'quarter' && <>
                    <div className="w-40" data-id="element-661">
                      <Select label="Quarter" value={selectedQuarter} onChange={e => setSelectedQuarter(e.target.value)} options={[{
                  value: '1',
                  label: 'Q1 (Jan-Mar)'
                }, {
                  value: '2',
                  label: 'Q2 (Apr-Jun)'
                }, {
                  value: '3',
                  label: 'Q3 (Jul-Sep)'
                }, {
                  value: '4',
                  label: 'Q4 (Oct-Dec)'
                }]} data-id="element-662" />
                    </div>
                    <div className="w-32" data-id="element-663">
                      <Select label="Year" value={selectedYear} onChange={e => setSelectedYear(e.target.value)} options={[{
                  value: '2024',
                  label: '2024'
                }, {
                  value: '2023',
                  label: '2023'
                }]} data-id="element-664" />
                    </div>
                  </>}

                {filterType === 'year' && <div className="w-32" data-id="element-665">
                    <Select label="Year" value={selectedYear} onChange={e => setSelectedYear(e.target.value)} options={[{
                value: '2024',
                label: '2024'
              }, {
                value: '2023',
                label: '2023'
              }]} data-id="element-666" />
                  </div>}

                {filterType === 'custom' && <div className="flex items-center gap-4" data-id="element-667">
                    <DatePicker label="Start Date" selected={startDate} onChange={setStartDate} data-id="element-668" />
                    <DatePicker label="End Date" selected={endDate} onChange={setEndDate} data-id="element-669" />
                  </div>}
              </div>}
          </div>
        </Card>

        {/* Transactions */}
        <Card data-id="element-670">
          <div className="flex items-center justify-between mb-4" data-id="element-671">
            <h3 className="text-base sm:text-lg font-bold text-gray-900" data-id="element-672">
              Transaction History
            </h3>
            <span className="text-sm text-gray-500" data-id="element-673">
              Showing {filteredTransactions.length} transactions
            </span>
          </div>

          {filteredTransactions.length === 0 ? <div className="text-center py-8 sm:py-12" data-id="element-674">
              <p className="text-sm sm:text-base text-gray-500" data-id="element-675">
                No transactions found for selected period
              </p>
            </div> : <div className="space-y-1" data-id="element-676">
              {filteredTransactions.map(txn => <div key={txn.id} className="flex items-center justify-between p-2 sm:p-3 hover:bg-gray-50 rounded-lg transition-colors gap-3" data-id="element-677">
                  <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0" data-id="element-678">
                    <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${txn.amount < 0 ? 'bg-green-100' : 'bg-gray-100'}`} data-id="element-679">
                      {txn.amount < 0 ? <div className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" data-id="element-680" /> : <div className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" data-id="element-681" />}
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-682">
                      <p className="text-sm sm:text-base font-medium text-gray-900 truncate" data-id="element-683">
                        {txn.merchant_name || txn.name}
                      </p>
                      <div className="flex flex-wrap items-center gap-1 sm:gap-2 mt-1" data-id="element-684">
                        <span className={`text-xs px-1.5 sm:px-2 py-0.5 rounded-full font-medium ${getCategoryColor(txn.personal_finance_category.primary)}`} data-id="element-685">
                          {txn.personal_finance_category.primary.replace(/_/g, ' ')}
                        </span>
                        <span className="text-xs text-gray-500" data-id="element-686">
                          {formatDate(txn.date)}
                        </span>
                        {txn.pending && <span className="text-xs px-1.5 sm:px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 font-medium" data-id="element-687">
                            Pending
                          </span>}
                      </div>
                    </div>
                  </div>
                  <div className="text-right ml-2 flex-shrink-0" data-id="element-688">
                    <p className={`text-sm sm:text-base font-semibold ${txn.amount < 0 ? 'text-green-600' : 'text-gray-900'}`} data-id="element-689">
                      {txn.amount < 0 ? '+' : '-'}
                      {formatCurrency(Math.abs(txn.amount))}
                    </p>
                    {txn.location.city && <p className="text-xs text-gray-500 mt-0.5 hidden sm:block" data-id="element-690">
                        {txn.location.city}, {txn.location.region}
                      </p>}
                  </div>
                </div>)}
            </div>}
        </Card>
      </div>;
  }
  // Account list view (unchanged)
  return <div className="space-y-4 sm:space-y-6" data-id="element-691">
      <div data-id="element-692">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1 sm:mb-2" data-id="element-693">
          Cash Flow
        </h1>
        <p className="text-sm sm:text-base text-gray-600" data-id="element-694">
          View your bank accounts and transactions
        </p>
      </div>

      {accounts.length === 0 ? <Card data-id="element-695">
          <div className="text-center py-8 sm:py-12" data-id="element-696">
            <div className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mx-auto mb-3 sm:mb-4" data-id="element-697" />
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2" data-id="element-698">
              No accounts found
            </h3>
            <p className="text-sm sm:text-base text-gray-600" data-id="element-699">
              Select a company to view their bank accounts
            </p>
          </div>
        </Card> : <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6" data-id="element-700">
          {accounts.map(account => <Card key={account.id} className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setSelectedAccount(account)} data-id="element-701">
              <div className="flex items-start justify-between mb-3 sm:mb-4" data-id="element-702">
                <div className="flex items-center gap-2 sm:gap-3" data-id="element-703">
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center ${account.type === 'checking' ? 'bg-blue-100' : 'bg-purple-100'}`} data-id="element-704">
                    {account.type === 'checking' ? <div className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" data-id="element-705" /> : <CreditCardIcon className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" data-id="element-706" />}
                  </div>
                  <div data-id="element-707">
                    <h3 className="text-sm sm:text-base font-semibold text-gray-900" data-id="element-708">
                      {account.name}
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-500" data-id="element-709">
                      {account.institution_name}
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2 sm:space-y-3" data-id="element-710">
                <div className="flex justify-between items-baseline" data-id="element-711">
                  <span className="text-xs sm:text-sm text-gray-500" data-id="element-712">
                    {account.type === 'checking' ? 'Current Balance' : 'Balance'}
                  </span>
                  <span className={`text-xl sm:text-2xl font-bold ${account.current_balance >= 0 ? 'text-gray-900' : 'text-red-600'}`} data-id="element-713">
                    {formatCurrency(Math.abs(account.current_balance))}
                  </span>
                </div>

                {account.type === 'credit_card' && <div className="flex justify-between items-baseline pt-2 sm:pt-3 border-t border-gray-100" data-id="element-714">
                    <span className="text-xs sm:text-sm text-gray-500" data-id="element-715">
                      Available Credit
                    </span>
                    <span className="text-base sm:text-lg font-semibold text-gray-900" data-id="element-716">
                      {formatCurrency(account.available_balance)}
                    </span>
                  </div>}

                <div className="pt-2 sm:pt-3 border-t border-gray-100" data-id="element-717">
                  <p className="text-xs text-gray-500" data-id="element-718">
                    Account •••• {account.mask}
                  </p>
                </div>
              </div>
            </Card>)}
        </div>}
    </div>;
}